import { ArrowUpRight } from 'lucide-react'

import { Badge } from '../Badge'
import { MdOutlineSystemUpdateAlt } from 'react-icons/md'
import texturaGradiente from '@/assets/textura-gradiente-granulada.jpg'

export type ChangelogEntry = {
  version: string
  date: string
  title: string
  description: string
  items?: string[]
  image?: string
  button?: {
    url: string
    text: string
  }
}

export interface Changelog1Props {
  title?: string
  description?: string
  entries?: ChangelogEntry[]
  className?: string
}

export const defaultEntries: ChangelogEntry[] = [
  {
    version: 'Version 1.3.0',
    date: '15 November 2024',
    title: 'Enhanced Analytics Dashboard',
    description:
      "We've completely redesigned our analytics dashboard to provide deeper insights and improved visualizations of your data.",
    items: [
      'Interactive data visualizations with real-time updates',
      'Customizable dashboard widgets',
      'Export analytics in multiple formats (CSV, PDF, Excel)',
      'New reporting templates for common use cases',
      'Improved data filtering and segmentation options',
    ],
    image: '/images/block/placeholder-aspect-video-1.svg',
    button: {
      url: 'https://shadcnblocks.com',
      text: 'Learn more',
    },
  },
]

export const Changelog1 = ({
  title = 'Changelog',
  description = 'Get the latest updates and improvements to our platform.',
  entries = defaultEntries,
}: Changelog1Props) => {
  return (
    <section className='relative min-h-screen bg-white'>
      {/* Header com imagem de fundo */}
      <div
        className='relative mx-auto px-4 pt-16 pb-32 text-center rounded-t-2xl md:px-8 md:pt-24 md:pb-26 overflow-hidden'
        style={{
          backgroundImage: `url(${texturaGradiente})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay para melhorar legibilidade */}
        <div className='absolute inset-0 bg-gradient-to-b from-blue-900/20 via-blue-800/30 to-blue-900/40' />
        
        <div className='relative z-10'>
          <h1 className='mb-4 text-3xl font-bold tracking-tight text-white drop-shadow-lg md:text-5xl'>
            {title}
          </h1>
          <p className='mb-6 text-base text-white/90 drop-shadow-md md:text-lg'>
            {description}
          </p>
        </div>
      </div>

      {/* Ícone com fundo rounded-full */}
      <div className='absolute left-1/2 -translate-x-1/2 translate-y-[-38px] z-50'>
        <div className='flex h-20 w-20 items-center justify-center rounded-full bg-white'>
          <MdOutlineSystemUpdateAlt className='size-10 text-blue-900' />
        </div>
      </div>

      {/* Cards de changelog */}
      <div className='mx-auto mt-16 max-w-5xl px-4 space-y-8 md:mt-20 md:space-y-12 pb-16'>
        {entries.map((entry, index) => (
          <div
            key={index}
            className='relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100 dark:border-gray-700'
          >
            <div className='flex flex-col gap-6 p-6 md:flex-row md:gap-8 md:p-8'>
              {/* Badge e data */}
              <div className='flex flex-row md:flex-col items-start md:items-center gap-3 md:gap-4 md:w-48 shrink-0'>
                <Badge
                  variant='success'
                  style='ghost'
                  className='text-xs bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 font-semibold'
                >
                  {entry.version}
                </Badge>
                <span className='text-xs font-medium text-gray-500 dark:text-gray-400'>
                  {entry.date}
                </span>
              </div>

              {/* Conteúdo */}
              <div className='flex-1 flex flex-col'>
                <h2 className='mb-3 text-xl leading-tight font-bold text-gray-900 dark:text-gray-100 md:text-2xl'>
                  {entry.title}
                </h2>
                <p className='text-sm text-gray-600 dark:text-gray-400 md:text-base leading-relaxed'>
                  {entry.description}
                </p>
                
                {entry.items && entry.items.length > 0 && (
                  <ul className='mt-6 space-y-2.5 text-sm text-gray-600 dark:text-gray-400 md:text-base'>
                    {entry.items.map((item, itemIndex) => (
                      <li key={itemIndex} className='flex items-start gap-3'>
                        <span className='inline-block mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0' />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {entry.image && (
                  <img
                    src={entry.image}
                    alt={`${entry.version} visual`}
                    className='mt-6 w-full rounded-xl object-cover shadow-md'
                  />
                )}
                
                {entry.button && (
                  <a
                    href={entry.button.url}
                    target='_blank'
                    rel='noreferrer'
                    className='mt-6 self-end inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors group'
                  >
                    {entry.button.text}
                    <ArrowUpRight className='h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform' />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
